/**
 * QuestionRenderer Component
 * Global rendering template for exam questions with explanations
 * 
 * Display Structure:
 * 1. Question
 * 2. Answer Options
 * 3. H1: Enhanced Explanation (starts with "The correct answer is: X")
 * 4. H2: Why the Other Options Are Incorrect
 * 5. Bold: Reference
 * 
 * Does NOT display: difficulty, cognitive level, validator results, regeneration data, internal metadata
 */

import { CheckCircle, XCircle, BookOpen } from 'lucide-react';

/**
 * Parse legacy explanation into structured parts
 * Handles both markdown format, plain text format, and HTML format
 */
const parseLegacyExplanation = (text) => {
  if (!text) return { correctAnswer: '', justification: '', incorrectExplanations: [], reference: null };
  
  let correctAnswer = '';
  let justification = '';
  let incorrectExplanations = [];
  let reference = null;
  
  // First, strip any HTML tags
  let cleanText = text
    .replace(/<[^>]+>/g, '')  // Remove HTML tags
    .replace(/&nbsp;/g, ' ')   // Replace HTML entities
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
  
  // Check if it's markdown format or plain text
  const hasMarkdown = cleanText.includes('##') || cleanText.includes('# ') || cleanText.includes('**');
  
  if (hasMarkdown) {
    // Handle markdown format
    // Extract correct answer first
    const correctMatch = cleanText.match(/##\s*Correct Answer[:\s]*\*?\*?_*([A-Z])/i) ||
                         cleanText.match(/The correct answer is[:\s]*([A-Z])/i) ||
                         cleanText.match(/Correct answer[:\s]*\*?\*?_*([A-Z])/i);
    if (correctMatch) {
      correctAnswer = correctMatch[1];
    }
    
    // Clean up the text by removing headers and markers
    let cleanedText = cleanText
      // Remove # Enhanced Explanation header
      .replace(/^#\s+Enhanced Explanation\s*/gim, '')
      // Remove ## Correct Answer **__X. Text__** pattern
      .replace(/##\s*Correct Answer\s*\*?\*?_*[A-Z][._]?\s*[^*_\n]+\*?\*?_*\s*/gi, '')
      // Replace Why Other Options section marker
      .replace(/##\s*Why\s*(the\s*)?Other\s*Options\s*Are\s*Incorrect\s*/gi, '\n|||INCORRECT|||\n')
      // Replace Reference section marker  
      .replace(/##\s*Reference\s*/gi, '\n|||REFERENCE|||\n')
      // Clean up bold/italic markers
      .replace(/\*\*_*|_*\*\*/g, '')
      .replace(/__/g, '')
      .trim();
    
    // Split by our markers
    const parts = cleanedText.split('|||');
    
    // First part is the main justification
    justification = parts[0]
      .replace(/The correct answer is[:\s]*[A-Z]\.?\s*/gi, '')
      .replace(/^-\s+/gm, '• ')
      .trim();
    
    // Find incorrect explanations section
    const incorrectIdx = parts.indexOf('INCORRECT');
    if (incorrectIdx !== -1 && parts[incorrectIdx + 1]) {
      const incorrectText = parts[incorrectIdx + 1].trim();
      
      // Parse each incorrect option (format: "- **A:** explanation" or "- A: explanation")
      const optionMatches = incorrectText.matchAll(/[-•]\s*\*?\*?([A-D])[:.]\*?\*?\s*([^\n-]+)/gi);
      for (const match of optionMatches) {
        incorrectExplanations.push([match[1].toUpperCase(), match[2].trim()]);
      }
    }
    
    // Find reference section
    const refIdx = parts.indexOf('REFERENCE');
    if (refIdx !== -1 && parts[refIdx + 1]) {
      const refText = parts[refIdx + 1].trim();
      const sourceMatch = refText.match(/\*?\*?Source[:\s]*\*?\*?\s*([^\n*]+)/i);
      if (sourceMatch) {
        reference = { source: sourceMatch[1].trim(), section: '' };
      } else {
        // Use whole line as source
        const firstLine = refText.split('\n')[0].replace(/\*\*/g, '').trim();
        if (firstLine) {
          reference = { source: firstLine, section: '' };
        }
      }
    }
    
    // Fallback reference extraction
    if (!reference) {
      const sourceMatch = cleanText.match(/\*?\*?Source[:\s]*\*?\*?\s*([^\n*]+)/i);
      if (sourceMatch) {
        reference = { source: sourceMatch[1].trim(), section: '' };
      }
    }
  } else {
    // Handle plain text format (legacy database questions)
    // Format: "Main explanation. Why the other options are incorrect: A: ...; B: ...; Reference: ..."
    
    // Extract main justification (everything before "Why the other options")
    const whyIncorrectMatch = cleanText.match(/^([\s\S]*?)(?:Why\s*(?:the\s*)?other\s*options\s*are\s*incorrect[:\s]*)/i);
    if (whyIncorrectMatch) {
      justification = whyIncorrectMatch[1].trim();
    } else {
      // No "Why the other options" section - use everything before Reference
      const refMatch = cleanText.match(/^([\s\S]*?)(?:Reference[:\s])/i);
      if (refMatch) {
        justification = refMatch[1].trim();
      } else {
        justification = cleanText;
      }
    }
    
    // Extract "Why the other options are incorrect" section
    const incorrectMatch = cleanText.match(/Why\s*(?:the\s*)?other\s*options\s*are\s*incorrect[:\s]*([\s\S]*?)(?:Reference[:\s]|$)/i);
    if (incorrectMatch) {
      const incorrectText = incorrectMatch[1];
      
      // First try to find explicit letter prefixes (A:, B:, C:)
      const letterMatches = incorrectText.match(/([A-D])[:.]\s*([^;]+)/gi);
      if (letterMatches) {
        letterMatches.forEach(m => {
          const parts = m.match(/([A-D])[:.]\s*(.+)/i);
          if (parts) {
            incorrectExplanations.push([parts[1].toUpperCase(), parts[2].trim()]);
          }
        });
      }
      
      // If no explicit letters found, try to parse by sentence patterns
      if (incorrectExplanations.length === 0) {
        const sentences = incorrectText.split(/[;.]/).filter(s => s.trim() && !s.toLowerCase().includes('reference'));
        let letter = 'A';
        sentences.forEach(sentence => {
          if (sentence.trim()) {
            incorrectExplanations.push([letter, sentence.trim()]);
            letter = String.fromCharCode(letter.charCodeAt(0) + 1);
          }
        });
      }
    }
    
    // Extract reference
    const refMatch = cleanText.match(/Reference[:\s]*(.+?)(?:\.|$)/i);
    if (refMatch) {
      reference = { source: refMatch[1].trim(), section: '' };
    }
  }
  
  return { correctAnswer, justification, incorrectExplanations, reference };
};

/**
 * ExplanationRenderer - Renders only the explanation portion
 * Use this when you only need the explanation (e.g., after answering in training mode)
 * 
 * Supports both nested explanation object and legacy flat fields
 */
export const ExplanationRenderer = ({
  question,
  isCorrect = null
}) => {
  if (!question) return null;

  // Convert answer ID to display label (c0 -> A, c1 -> B, etc.)
  const convertIdToLabel = (id) => {
    if (!id) return id;
    // If already a letter (A, B, C, D), return as-is
    if (id.length === 1 && /^[A-Z]$/i.test(id)) {
      return id.toUpperCase();
    }
    // Convert c0 -> A, c1 -> B, etc.
    if (id.startsWith('c') && id.length <= 3) {
      const idx = parseInt(id.substring(1), 10);
      if (!isNaN(idx) && idx >= 0 && idx < 26) {
        return String.fromCharCode(65 + idx); // A=65
      }
    }
    return id;
  };

  // Get correct answer labels (e.g., "A", "B", "C")
  const getCorrectAnswerLabels = () => {
    const correctAnswers = question.correct_answers || question.correctAnswers || [];
    if (Array.isArray(correctAnswers)) {
      // Convert each ID to display label
      return correctAnswers.map(convertIdToLabel).join(', ');
    }
    return convertIdToLabel(correctAnswers);
  };

  // Check if explanation is nested object (new format) or flat (legacy)
  const isNestedExplanation = () => {
    const expl = question.explanation;
    return expl && typeof expl === 'object' && !Array.isArray(expl) && 
           (expl.correct_justification || expl.incorrect_explanations || expl.reference);
  };

  // Build incorrect explanations section from structured data
  const buildIncorrectExplanations = () => {
    let incorrectExplanations = {};
    
    // NEW FORMAT: Check choices array with embedded explanations first
    if (question.choices && Array.isArray(question.choices)) {
      question.choices.forEach((choice, idx) => {
        if (!choice.is_correct && choice.explanation) {
          const label = choice.id || String.fromCharCode(65 + idx);
          incorrectExplanations[label] = choice.explanation;
        }
      });
      if (Object.keys(incorrectExplanations).length > 0) {
        // Filter out correct answers and return
        const correctAnswers = question.correct_answers || question.correctAnswers || question.correct_answer_ids || [];
        const correctSet = new Set(correctAnswers.map(a => convertIdToLabel(a)));
        return Object.entries(incorrectExplanations)
          .filter(([key]) => !correctSet.has(key));
      }
    }
    
    // NEW FORMAT: Check structured_explanation.why_distractors_wrong
    if (question.structured_explanation?.why_distractors_wrong) {
      const distractorsWrong = question.structured_explanation.why_distractors_wrong;
      if (typeof distractorsWrong === 'object') {
        Object.entries(distractorsWrong).forEach(([key, value]) => {
          incorrectExplanations[key] = value;
        });
        if (Object.keys(incorrectExplanations).length > 0) {
          const correctAnswers = question.correct_answers || question.correctAnswers || question.correct_answer_ids || [];
          const correctSet = new Set(correctAnswers.map(a => convertIdToLabel(a)));
          return Object.entries(incorrectExplanations)
            .filter(([key]) => !correctSet.has(key));
        }
      }
    }
    
    // NEW FORMAT: Check distractor_explanations array
    if (question.distractor_explanations && Array.isArray(question.distractor_explanations)) {
      const distractors = question.distractors || [];
      question.distractor_explanations.forEach((expl, idx) => {
        // Map to B, C, D, E (since A is typically correct)
        const label = String.fromCharCode(66 + idx);
        incorrectExplanations[label] = expl;
      });
      if (Object.keys(incorrectExplanations).length > 0) {
        return Object.entries(incorrectExplanations);
      }
    }
    
    // Check nested explanation object (legacy new format)
    if (isNestedExplanation()) {
      incorrectExplanations = question.explanation.incorrect_explanations || {};
    } else {
      // Legacy: flat field
      incorrectExplanations = question.incorrect_explanations || 
                              question.raw_question_json?.incorrect_explanations || {};
    }
    
    const correctAnswers = question.correct_answers || question.correctAnswers || [];
    
    // Convert correct answers to both formats for filtering (A and c0)
    const correctSet = new Set();
    correctAnswers.forEach(ans => {
      correctSet.add(ans);
      correctSet.add(convertIdToLabel(ans));
      // Also add c-format if it's a letter
      if (ans.length === 1 && /^[A-Z]$/i.test(ans)) {
        const idx = ans.toUpperCase().charCodeAt(0) - 65;
        correctSet.add(`c${idx}`);
      }
    });
    
    // Filter out correct answers from explanations
    const filtered = Object.entries(incorrectExplanations)
      .filter(([key]) => !correctSet.has(key) && !correctSet.has(convertIdToLabel(key)));
    
    return filtered;
  };

  // Get reference information
  const getReference = () => {
    let ref = null;
    
    // Check nested explanation object first (new format)
    if (isNestedExplanation()) {
      ref = question.explanation.reference;
    } else {
      // Legacy: flat field
      ref = question.reference || question.raw_question_json?.reference;
    }
    
    if (!ref) return null;
    return {
      source: ref.source || ref.normative_source || '',
      performance_domain: ref.performance_domain || '',
      section: ref.section || ''
    };
  };

  // Get correct justification
  const getCorrectJustification = () => {
    // NEW FORMAT: Check choices array for correct answer explanation
    if (question.choices && Array.isArray(question.choices)) {
      const correctChoice = question.choices.find(c => c.is_correct);
      if (correctChoice?.explanation) {
        return correctChoice.explanation;
      }
    }
    
    // NEW FORMAT: Check structured_explanation.correct_answer_rationale
    if (question.structured_explanation?.correct_answer_rationale) {
      return question.structured_explanation.correct_answer_rationale;
    }
    
    // NEW FORMAT: Check correct_answer_explanation (flat field)
    if (question.correct_answer_explanation) {
      return question.correct_answer_explanation;
    }
    
    // Check nested explanation object (legacy new format)
    if (isNestedExplanation()) {
      return question.explanation.correct_justification || '';
    }
    
    // Legacy: flat field
    return question.correct_justification || 
           question.raw_question_json?.correct_justification ||
           '';
  };

  // Check if we have structured data or need to parse legacy markdown
  const hasStructuredData = isNestedExplanation() ||
                            question.correct_justification || 
                            question.raw_question_json?.correct_justification ||
                            question.structured_explanation ||
                            question.correct_answer_explanation ||
                            question.distractor_explanations ||
                            (question.choices && question.choices.some(c => c.explanation)) ||
                            (question.incorrect_explanations && Object.keys(question.incorrect_explanations).length > 0);
  
  let correctLabels = getCorrectAnswerLabels();
  let justification = '';
  let incorrectExplanations = [];
  let reference = null;

  if (hasStructuredData) {
    // Use structured data
    justification = getCorrectJustification() || question.explanation || '';
    incorrectExplanations = buildIncorrectExplanations();
    reference = getReference();
  } else {
    // Parse legacy markdown explanation
    const explanationText = typeof question.explanation === 'string' ? question.explanation : '';
    const parsed = parseLegacyExplanation(explanationText);
    if (parsed.correctAnswer && !correctLabels) {
      correctLabels = parsed.correctAnswer;
    }
    justification = parsed.justification;
    incorrectExplanations = parsed.incorrectExplanations;
    reference = parsed.reference || getReference();
  }

  return (
    <div className="explanation-renderer space-y-4">
      {/* Result indicator */}
      {isCorrect !== null && (
        <div className={`flex items-center gap-2 ${isCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {isCorrect ? (
            <>
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">Correct!</span>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5" />
              <span className="font-semibold">Incorrect</span>
            </>
          )}
        </div>
      )}

      {/* H1: Enhanced Explanation */}
      <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
        <h4 className="text-lg font-bold text-emerald-900 dark:text-emerald-100 mb-3 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Enhanced Explanation
        </h4>
        
        {/* Correct Answer Statement */}
        {correctLabels && (
          <p className="font-semibold text-emerald-800 dark:text-emerald-200 mb-3">
            The correct answer is: <span className="text-emerald-600 dark:text-emerald-400">{correctLabels}</span>
          </p>
        )}
        
        {/* Full Conceptual Explanation */}
        <div className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap text-sm">
          {justification || 'No explanation available.'}
        </div>
      </div>

      {/* H2: Why the Other Options Are Incorrect */}
      {incorrectExplanations.length > 0 && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-xl border border-amber-200 dark:border-amber-800">
          <h5 className="text-base font-semibold text-amber-900 dark:text-amber-100 mb-3">
            Why the Other Options Are Incorrect
          </h5>
          
          <div className="space-y-2">
            {incorrectExplanations.map(([key, explanation]) => (
              <div key={key} className="flex gap-2 text-sm">
                <span className="font-bold text-amber-700 dark:text-amber-300 min-w-[24px]">
                  {key}:
                </span>
                <span className="text-slate-700 dark:text-slate-300">
                  {explanation}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bold: Reference */}
      {reference && (reference.source || reference.performance_domain || reference.section) && (
        <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
          <p className="text-sm">
            <span className="font-bold text-slate-700 dark:text-slate-300">Reference: </span>
            <span className="text-slate-600 dark:text-slate-400">
              {reference.source}
              {reference.performance_domain && ` – ${reference.performance_domain}`}
              {reference.section && ` – ${reference.section}`}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

const QuestionRenderer = ({
  question,
  questionIndex,
  userAnswer,
  showExplanation = true,
  isReview = false,
  hideQuestionHeader = false
}) => {
  if (!question) return null;

  // Convert c0/c1/c2 to A/B/C format
  const convertChoiceId = (id, idx) => {
    if (!id) return String.fromCharCode(65 + idx); // A, B, C...
    // Handle c0, c1, c2 format
    if (typeof id === 'string' && /^c\d+$/.test(id)) {
      const num = parseInt(id.substring(1), 10);
      return String.fromCharCode(65 + num); // c0 -> A, c1 -> B, etc.
    }
    return id;
  };

  // Normalize choices to consistent format
  const normalizeChoices = (choices, answers) => {
    // Handle dict format {"A": "text", "B": "text"}
    if (answers && typeof answers === 'object' && !Array.isArray(answers)) {
      return Object.entries(answers).map(([id, text]) => ({ id: convertChoiceId(id, 0), text }));
    }
    // Handle array format
    if (!choices || !Array.isArray(choices)) return [];
    return choices.map((choice, idx) => {
      if (typeof choice === 'string') {
        return { id: String.fromCharCode(65 + idx), text: choice };
      }
      const rawId = choice.id || choice.choice_id;
      return {
        id: convertChoiceId(rawId, idx),
        text: choice.text || choice.choice_text || choice
      };
    });
  };

  // Normalize correct answers to A/B/C format
  const normalizeCorrectAnswers = (answers) => {
    if (!answers) return [];
    const answerArray = Array.isArray(answers) ? answers : [answers];
    return answerArray.map((ans, idx) => convertChoiceId(ans, idx));
  };

  const choices = normalizeChoices(question.choices, question.answers);
  const questionText = question.question_text || question.questionText || question.raw_question_json?.question_text || '';
  const scenario = question.scenario || question.raw_question_json?.scenario || '';
  const rawCorrectAnswers = question.correct_answers || question.correctAnswers || question.raw_question_json?.correct_answers || [];
  const correctAnswers = normalizeCorrectAnswers(rawCorrectAnswers);

  return (
    <div className="question-renderer space-y-6" data-testid={`question-renderer-${questionIndex}`}>
      {/* 1. Question */}
      <div className="question-section">
        {!hideQuestionHeader && questionIndex !== undefined && (
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">
            Question {questionIndex + 1}
          </h3>
        )}
        
        {/* Scenario (if present) */}
        {scenario && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
            <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
              {scenario}
            </p>
          </div>
        )}
        
        {/* Question Text */}
        <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed text-base">
          {questionText}
        </p>
      </div>

      {/* 2. Answer Options */}
      <div className="answers-section space-y-2">
        {choices.map((choice) => {
          const isUserAnswer = userAnswer?.includes(choice.id);
          const isCorrectAnswer = correctAnswers.includes(choice.id);
          
          let bgClass = 'bg-slate-50 dark:bg-slate-800';
          let borderClass = 'border-slate-200 dark:border-slate-700';
          
          if (isReview) {
            if (isCorrectAnswer) {
              bgClass = 'bg-green-50 dark:bg-green-950/30';
              borderClass = 'border-green-300 dark:border-green-800';
            } else if (isUserAnswer && !isCorrectAnswer) {
              bgClass = 'bg-red-50 dark:bg-red-950/30';
              borderClass = 'border-red-300 dark:border-red-800';
            }
          }
          
          return (
            <div
              key={choice.id}
              className={`p-3 rounded-lg border ${bgClass} ${borderClass} transition-colors`}
              data-testid={`answer-option-${choice.id}`}
            >
              <div className="flex items-start gap-3">
                {isReview && (
                  <div className="mt-0.5">
                    {isCorrectAnswer ? (
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    ) : isUserAnswer ? (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    ) : (
                      <div className="w-5 h-5" />
                    )}
                  </div>
                )}
                <div className="flex-1">
                  <span className="font-semibold text-slate-700 dark:text-slate-300 mr-2">
                    {choice.id}:
                  </span>
                  <span className="text-slate-700 dark:text-slate-300">
                    {choice.text}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 3, 4, 5: Enhanced Explanation, Incorrect Options, Reference */}
      {showExplanation && (
        <ExplanationRenderer question={question} />
      )}
    </div>
  );
};

export default QuestionRenderer;
